import React from "react";
import { Box, Typography } from "@material-ui/core";
import type { CoreCruisesCheckout } from "redmond";

import { CheckoutBreadcrumbs } from "./CheckoutBreadcrumbs";

interface Props {
  core?: CoreCruisesCheckout;
}

/**
 * A titled placeholder for a checkout section. Renders the section's heading
 * (and optional step label) plus a short note of what it will contain — the
 * real workflow/components get built in piece by piece.
 */
const SectionPlaceholder = ({
  step,
  title,
  children,
}: {
  step?: string;
  title: string;
  children: React.ReactNode;
}) => (
  <Box
    component="section"
    style={{
      background: "#fff",
      border: "1px solid #e4e4e4",
      borderRadius: 8,
      padding: 24,
      marginBottom: 16,
    }}
  >
    {step != null && (
      <Typography
        variant="overline"
        style={{ color: "#6b6b6b", letterSpacing: 0.5 }}
      >
        {step}
      </Typography>
    )}
    <Typography variant="h3" style={{ margin: "4px 0 8px" }}>
      {title}
    </Typography>
    <Typography variant="body2" style={{ color: "#6b6b6b" }}>
      {children}
    </Typography>
  </Box>
);

/**
 * CruiseCheckoutLayout — scaffold of the cruise checkout page, mirroring the
 * activities book layout (a main checkout-steps column beside a sticky summary
 * sidebar). Each of the 7 sections is a titled placeholder describing what it
 * will hold; the real components get built in piece by piece.
 *
 * Figma: Cruises / Checkout (node 3359-497148).
 */
export const CruiseCheckoutLayout = ({ core }: Props) => {
  const sailingName = core?.sailing?.sailingName;

  return (
    <Box style={{ maxWidth: 1312, margin: "0 auto", padding: 24 }}>
      <CheckoutBreadcrumbs />

      <Box style={{ display: "flex", gap: 24, alignItems: "flex-start" }}>
        {/* Main checkout-steps column */}
        <Box style={{ flex: 1, minWidth: 0 }}>
          <SectionPlaceholder step="Step 1" title="Assign travelers to cabins">
            Assign each saved traveler to a cabin; collapses to a summary of the
            assigned guests once complete.
          </SectionPlaceholder>

          <SectionPlaceholder step="Step 2" title="Primary traveler information">
            The lead guest&apos;s details and contact info used for the
            reservation; collapses to a summary once complete.
          </SectionPlaceholder>

          <SectionPlaceholder step="Step 3" title="Rewards and payment">
            The payment option (deposit now vs. pay in full), travel credits,
            rewards accounts, and payment method — plus the terms agreement and
            the Confirm &amp; book action.
          </SectionPlaceholder>

          <SectionPlaceholder title="Cancellation policy">
            The sailing-specific cancellation terms and refund schedule.
          </SectionPlaceholder>

          <SectionPlaceholder title="Contact information">
            Support channels (call / email) for questions about the booking.
          </SectionPlaceholder>

          <SectionPlaceholder title="Sustainability">
            The &ldquo;trees planted for this booking&rdquo; message.
          </SectionPlaceholder>
        </Box>

        {/* Sticky summary sidebar */}
        <Box
          component="aside"
          style={{ position: "sticky", top: 24, width: 400, flexShrink: 0 }}
        >
          <SectionPlaceholder title="Checkout summary">
            Sailing media, trip summary (line, ship, dates, ports), the checkout
            price breakdown per cabin, rewards/loyalty earn, and the Confirm
            &amp; book CTA
            {sailingName != null ? ` — ${sailingName}` : ""}.
          </SectionPlaceholder>
        </Box>
      </Box>
    </Box>
  );
};
